// Shared helpers for task list API responses and in-memory task collections.
//
// The task endpoints return data in several shapes (bare array, or wrapped
// under tasks/items/results/data), and several screens keep their own copy of
// the same merge/remove/upsert logic. These helpers centralise what was
// previously duplicated across ProjectService, the Project page and Statistics.

// Unwrap a task list payload regardless of which envelope key the backend used.
export function extractTaskList(data: any): any[] {
    if (Array.isArray(data)) return data
    if (Array.isArray(data?.tasks)) return data.tasks
    if (Array.isArray(data?.items)) return data.items
    if (Array.isArray(data?.results)) return data.results
    if (Array.isArray(data?.data?.tasks)) return data.data.tasks
    if (Array.isArray(data?.data)) return data.data
    return []
}

// Merge task lists by id, later lists winning. Tasks without an id are dropped,
// matching the previous per-screen behaviour.
export function mergeTasks(...taskLists: any[][]): any[] {
    const taskMap = new Map<string, any>()

    taskLists.flat().forEach((task) => {
        if (task?.id == null) return

        taskMap.set(String(task.id), task)
    })

    return Array.from(taskMap.values())
}

export function removeTaskById(taskList: any[], taskId?: string | number | null): any[] {
    if (taskId == null) return taskList

    return taskList.filter((task) => String(task.id) !== String(taskId))
}

// Replace a task in place when it is already present, otherwise append it.
// The existing entry is shallow-merged so partial updates keep unknown fields.
export function upsertTask(taskList: any[], task: any): any[] {
    const taskIndex = taskList.findIndex((currentTask) => String(currentTask.id) === String(task.id))

    if (taskIndex === -1) {
        return [...taskList, task]
    }

    const nextTaskList = [...taskList]
    nextTaskList[taskIndex] = {
        ...nextTaskList[taskIndex],
        ...task,
    }

    return nextTaskList
}

export function isArchivedTask(task: any): boolean {
    return Boolean(task?.is_archived || task?.archived_at)
}

export function filterArchivedTasks(taskList: any[]): any[] {
    return taskList.filter((task) => !isArchivedTask(task))
}
